import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
} from 'amazon-cognito-identity-js';

import { config } from './config';
import { CODE_ON_ITS_WAY, CODE_REQUEST_FAILED, LINK_REQUEST_FAILED } from './joining';

export interface Session {
  idToken: string;
  accountId: string;
  // 'operator' is the internal ISO admin-console principal (DECISION-059) — a
  // third account type the shell routes to its own console, off the two rails.
  accountType: 'hotel' | 'supplier' | 'operator';
  name: string;
}

// Session-restore state is never gated on a UX convenience flag: the session
// always lands in sessionStorage. (localStorage "remember" can come later —
// the desk rule is a 16h refresh TTL, not a 90-day one.)
const KEY = 'iso.session';

export function currentSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as Session;
    const claims = JSON.parse(atob(s.idToken.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
    if (claims.exp * 1000 < Date.now()) return null;
    return s;
  } catch {
    return null;
  }
}

// N40: the invitation's yes signs the supplier in on the desk too, with the same
// token set — stored the same way.
export function storeSession(idToken: string): Session {
  return store(idToken);
}

function store(idToken: string): Session {
  const claims = JSON.parse(atob(idToken.split('.')[1]!.replace(/-/g, '+').replace(/_/g, '/')));
  const session: Session = {
    idToken,
    accountId: claims['custom:accountId'],
    accountType: claims['custom:accountType'],
    name: claims.email ?? claims.phone_number ?? 'you',
  };
  sessionStorage.setItem(KEY, JSON.stringify(session));
  return session;
}

export function signOut(): void {
  sessionStorage.removeItem(KEY);
}

// The remembered account id. Written to localStorage only when "remember" is
// ticked, but ALWAYS to sessionStorage — session restore is never gated on a
// UX convenience flag.
const ACCOUNT_KEY = 'iso.accountId';

export function rememberedAccountId(): string {
  return sessionStorage.getItem(ACCOUNT_KEY) ?? localStorage.getItem(ACCOUNT_KEY) ?? '';
}

export function storeAccountId(accountId: string, remember: boolean): void {
  sessionStorage.setItem(ACCOUNT_KEY, accountId);
  if (remember) localStorage.setItem(ACCOUNT_KEY, accountId);
  else localStorage.removeItem(ACCOUNT_KEY);
}

// The typed account id and the token's tenant must agree — a credential from
// another account signing in "under" this one is an error, not a curiosity.
export function assertAccountMatch(session: Session, accountId: string): Session {
  if (session.accountId !== accountId) {
    signOut();
    throw new Error('Those credentials belong to a different account.');
  }
  return session;
}

// Hotel staff: SRP against the pool. The password never crosses the wire.
export function signInHotel(email: string, password: string): Promise<Session> {
  const c = config();
  const pool = new CognitoUserPool({ UserPoolId: c.userPoolId, ClientId: c.platformClientId });
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.authenticateUser(new AuthenticationDetails({ Username: email, Password: password }), {
      onSuccess: (result) => resolve(store(result.getIdToken().getJwtToken())),
      onFailure: () => reject(new Error('That did not work. Check the email and password.')),
      newPasswordRequired: () => reject(new Error('This account needs a new password set by an admin.')),
    });
  });
}

// Suppliers: the SMS-code flow through the API. No password exists. The API
// reads the number however it was typed (B34) and answers with the number the
// code went to.
export interface CodeSent {
  challengeId: string;
  phone: string;
  resendAfterSeconds: number;
  // The one line every number gets (DECISION-080 §10), as the API says it.
  message: string;
}

export async function requestCode(phone: string): Promise<CodeSent> {
  const c = config();
  let res: Response;
  try {
    res = await fetch(`${c.apiBase}/auth/code`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ phone, surface: 'platform' }),
    });
  } catch {
    throw new Error('No connection right now — try again in a moment.');
  }
  if (!res.ok) {
    // A number no code can reach is answered in the supplier's words, the same
    // for every number — one the API can't read (B34), or one that is not a
    // Greek mobile (DECISION-080 §8) — so it is shown as it comes. Anything else
    // broke on our side (B31): never "Check the number", because the number
    // was fine. A refused text never lands here: the API answers it with the
    // same 202 as any number, so the page never says who has an account.
    const refused = await res.json().catch(() => ({}));
    throw new Error(res.status === 422 && typeof refused.message === 'string' ? refused.message : CODE_REQUEST_FAILED);
  }
  const body = await res.json();
  return {
    challengeId: body.challengeId as string,
    phone: typeof body.phone === 'string' ? body.phone : phone,
    resendAfterSeconds: Number(body.resendAfterSeconds) || 30,
    message: typeof body.message === 'string' && body.message ? body.message : CODE_ON_ITS_WAY,
  };
}

// A code that did not sign in, and what that leaves (B34): `wrong` — type it
// again, against `challengeId`, the next live try the API handed back; any
// other kind — this code is finished, and only a new one goes on.
export class CodeRefused extends Error {
  constructor(
    public kind: 'wrong' | 'too_many' | 'expired' | 'lost',
    message: string,
    public challengeId = '',
  ) {
    super(message);
    this.name = 'CodeRefused';
  }
}

// N40 — a number with no account but a hotel's open invitation: the right code
// proves the phone and hands back its invitation (canon 09), so the desk opens
// it, phone proven, and the yes there needs no second code.
export interface InvitationWaiting {
  invitation: string;
  challengeId: string;
}

export async function verifyCode(challengeId: string, code: string): Promise<Session | InvitationWaiting> {
  const c = config();
  let res: Response;
  try {
    res = await fetch(`${c.apiBase}/auth/verify`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ challengeId, code }),
    });
  } catch {
    // The connection dropped mid-check: whether the try counted can't be known
    // from here, so the honest way on is a new code.
    throw new CodeRefused('lost', 'The connection dropped while we checked your code. Tap Send again for a new one.');
  }
  const body = await res.json().catch(() => ({}));
  if (res.ok && body.next === 'invitation' && typeof body.invitation === 'string') {
    return { invitation: body.invitation as string, challengeId: body.challengeId as string };
  }
  if (res.ok) return store(body.idToken as string);
  const message = typeof body.message === 'string' ? body.message : '';
  if (body.code === 'code_wrong' && typeof body.details?.challengeId === 'string') {
    throw new CodeRefused('wrong', message, body.details.challengeId);
  }
  if (body.code === 'too_many_tries') throw new CodeRefused('too_many', message || 'Too many tries — tap Send again for a new code.');
  if (res.status === 401) throw new CodeRefused('expired', message || 'This code has expired — tap Send again for a new code.');
  throw new Error("That didn't go through — try the code again in a moment.");
}

// The magic link: first-class for shared desk machines (canon 09, Entry).
// Request always looks the same from the outside — existence is never
// disclosed — and the emailed token redeems on any browser.
export async function requestMagicLink(email: string): Promise<void> {
  const c = config();
  const res = await fetch(`${c.apiBase}/auth/code`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ email, surface: 'platform' }),
  });
  if (res.ok) return;
  // An address that does not read as one is said as the API says it. Anything
  // else broke on our side (B31) — a refused email is answered with the same 202
  // as any address, so it never lands here, and the address is never blamed.
  const refused = await res.json().catch(() => ({}));
  throw new Error(res.status === 422 && typeof refused.message === 'string' ? refused.message : LINK_REQUEST_FAILED);
}

export async function redeemLink(token: string): Promise<Session> {
  const c = config();
  const res = await fetch(`${c.apiBase}/auth/verify`, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ linkToken: token }),
  });
  if (!res.ok) throw new Error('This link has expired or was already used.');
  const body = await res.json();
  return store(body.idToken as string);
}
